/**
 * ProgressView — Operational data source dashboard
 *
 * Replaces the old "Data Source Ready" / stepper UI with a compact
 * operational layout inspired by Fivetran / Stripe dashboards.
 *
 * Optimistic state: isSyncStarting (set immediately on click, cleared when polled run_id matches)
 *   pendingRunIdRef guards against stale poll data resetting the optimistic state.
 *
 * ┌──────────────────┬──────────────┬──────────────────────────────────┬──────────────┬───────────┬─────────────┬─────────────┬─────────────┬──────────┐
 * │ status           │ stage        │ Status Indicator                 │ Reauth       │ Sync Info │ Progress    │ Dataset     │ Edit Tables │ Sync Now │
 * │                  │              │                                  │ Banner       │ Panel     │ Bar         │ Table       │             │          │
 * ├──────────────────┼──────────────┼──────────────────────────────────┼──────────────┼───────────┼─────────────┼─────────────┼─────────────┼──────────┤
 * │ (optimistic)     │ —            │ 🔄 "Starting sync…"              │ ❌           │ ❌        │ ❌ spinner  │ ✅ (Waiting) │ ✅          │ disabled │
 * │ syncing          │ ingesting/…  │ 🔄 "Syncing 3/8"                 │ ❌           │ ❌        │ ✅          │ ✅ (live)    │ ✅          │ Cancel   │
 * │ syncing          │ cancelled    │ ⚠️ "Cancelled"                   │ ❌           │ ❌        │ ❌          │ ✅ (Waiting) │ ✅          │ Cancel   │
 * │ ready            │ done         │ ✅ "Last synced X ago"           │ ❌           │ ✅        │ ❌          │ ✅           │ ✅          │ ✅       │
 * │ ready            │ cancelled    │ ✅ "Last synced X ago (cancell)" │ ❌           │ ✅        │ ❌          │ ✅ (Waiting) │ ✅          │ ✅       │
 * │ error            │ error        │ ❌ "Error"                       │ ❌           │ ✅ +err   │ ❌          │ ✅           │ ✅          │ ✅       │
 * │ reauth_required  │ error        │ 🔒 "Reconnection required"       │ ✅ Reconnect │ ❌        │ ❌          │ ❌           │ 🚫 disabled │ 🚫 dis  │
 * └──────────────────┴──────────────┴──────────────────────────────────┴──────────────┴───────────┴─────────────┴─────────────┴─────────────┴──────────┘
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Body1,
  Caption1,
  Spinner,
  ProgressBar,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  Checkmark24Regular,
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  ErrorCircle24Regular,
  Clock24Regular,
  ArrowSync24Regular,
  TableEditRegular,
  LockClosed24Regular,
  BrainCircuit24Regular,
} from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import { datasetMessageDisplayName, ingestionDatasetLabels, selectedEntityLabels } from '../../utils/datasetDisplayName';
import { ingestionDatasetDisplay, type DatasetStatus } from '../../utils/ingestionDatasetDisplay';
import { dataModelsService } from '../../api/dataModelsService';
import { useIngestionProgress } from './useIngestionProgress';
import { ingestionProgressRows } from './ingestionProgressRows';
import { configuredSyncModes, syncModeLabel, type ConfiguredSyncMode } from './connectionSyncMode';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useDataModels } from '../../contexts/DataModelsContext';
import { useTopBarActionItems } from '../../contexts/TopBarContext';
import type { TopBarAction } from '../../contexts/TopBarContext';
import type { IngestionDatasetProgress } from '../../api/types';
import { MutationButton, useWorkspaceAccess } from '../security/WorkspaceAccess';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDuration(startedAt: string | null | undefined, updatedAt: string | null | undefined): string {
  if (!startedAt || !updatedAt) return '—';
  const ms = new Date(updatedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainSeconds = seconds % 60;
  return `${minutes}m ${remainSeconds}s`;
}

// ---------------------------------------------------------------------------
// StatusIndicator (colored icon + text, no badge)
// ---------------------------------------------------------------------------

const StatusIndicator: React.FC<{
  isSyncing: boolean;
  isSyncStarting: boolean;
  isError: boolean;
  isReauthRequired: boolean;
  wasCancelled: boolean;
  completedCount: number;
  totalCount: number;
  lastSyncedAt?: string | null;
}> = ({ isSyncing, isSyncStarting, isError, isReauthRequired, wasCancelled, completedCount, totalCount, lastSyncedAt }) => {
  // Transient: server confirms syncing + cancelled → show "Cancelled"
  // (but NOT during isSyncStarting — stale poll data should be ignored)
  if (isSyncing && !isSyncStarting && wasCancelled) {
    return (
      <div className="pv-status-indicator">
        <DismissCircle24Regular style={{ fontSize: 16, color: tokens.colorStatusWarningForeground1 }} />
        <Text size={300} style={{ color: tokens.colorStatusWarningForeground1 }}>Cancelled</Text>
      </div>
    );
  }
  if (isSyncing) {
    return (
      <div className="pv-status-indicator">
        <Spinner size="extra-tiny" />
        <Text size={300} style={{ color: tokens.colorBrandForeground1 }}>
          {isSyncStarting ? 'Starting sync…' : `Syncing ${completedCount}/${totalCount}`}
        </Text>
      </div>
    );
  }
  if (isReauthRequired) {
    return (
      <div className="pv-status-indicator">
        <LockClosed24Regular style={{ fontSize: 16, color: tokens.colorStatusWarningForeground1 }} />
        <Text size={300} style={{ color: tokens.colorStatusWarningForeground1 }}>Reconnection required</Text>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="pv-status-indicator">
        <ErrorCircle24Regular style={{ fontSize: 16, color: tokens.colorStatusDangerForeground1 }} />
        <Text size={300} style={{ color: tokens.colorStatusDangerForeground1 }}>Error</Text>
      </div>
    );
  }
  // Terminal: ready + cancelled → "Last synced X ago (cancelled)"
  // Terminal: ready + done → "Last synced X ago"
  return (
    <div className="pv-status-indicator">
      <CheckmarkCircle24Regular style={{ fontSize: 16, color: tokens.colorStatusSuccessForeground1 }} />
      <Text size={300} style={{ color: tokens.colorStatusSuccessForeground1 }}>
        Last synced {timeAgo(lastSyncedAt)}
      </Text>
      {wasCancelled && (
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>(cancelled)</Text>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SyncInfoPanel
// ---------------------------------------------------------------------------

const SyncInfoPanel: React.FC<{
  startedAt: string | null | undefined;
  updatedAt: string | null | undefined;
  datasets: IngestionDatasetProgress[];
  totalRowCount: number | undefined;
  error: string | null | undefined;
}> = ({ startedAt, updatedAt, datasets, totalRowCount, error }) => {
  const errorCount = datasets.filter((d) => d.stage === 'error').length;

  return (
    <div className="pv-sync-info">
      <div className="pv-sync-info-item">
        <Caption1 className="pv-sync-info-label">Last Sync</Caption1>
        <Text weight="semibold" size={300}>{timeAgo(updatedAt)}</Text>
      </div>
      <div className="pv-sync-info-item">
        <Caption1 className="pv-sync-info-label">Duration</Caption1>
        <Text weight="semibold" size={300}>{formatDuration(startedAt, updatedAt)}</Text>
      </div>
      <div className="pv-sync-info-item">
        <Caption1 className="pv-sync-info-label">Tables</Caption1>
        <Text weight="semibold" size={300}>{datasets.length}</Text>
      </div>
      <div className="pv-sync-info-item">
        <Caption1 className="pv-sync-info-label">Total Rows</Caption1>
        <Text weight="semibold" size={300}>{totalRowCount != null ? totalRowCount.toLocaleString() : '—'}</Text>
      </div>
      <div className="pv-sync-info-item">
        <Caption1 className="pv-sync-info-label">Errors</Caption1>
        <Text weight="semibold" size={300} style={errorCount > 0 ? { color: tokens.colorStatusDangerForeground1 } : undefined}>
          {errorCount}
        </Text>
      </div>
      {error && (
        <div className="pv-sync-info-error">
          <ErrorCircle24Regular style={{ fontSize: 16, color: tokens.colorStatusDangerForeground1 }} />
          <Caption1 style={{ color: tokens.colorStatusDangerForeground1 }}>{error}</Caption1>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SyncProgressBar
// ---------------------------------------------------------------------------

const SyncProgressBar: React.FC<{
  progress: number;
  completedCount: number;
  totalCount: number;
}> = ({ progress, completedCount, totalCount }) => (
  <div className="pv-overall-progress">
    <div className="pv-overall-progress-header">
      <Caption1><strong>Overall Sync Progress</strong></Caption1>
      <Caption1>{completedCount} / {totalCount} tables synced</Caption1>
    </div>
    <ProgressBar value={progress} thickness="large" color="brand" />
  </div>
);


// ---------------------------------------------------------------------------
// DatasetTable
// ---------------------------------------------------------------------------

const DatasetStatusIcon: React.FC<{ status: DatasetStatus }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return <Checkmark24Regular style={{ fontSize: 16, color: tokens.colorStatusSuccessForeground1 }} />;
    case 'syncing':
      return <Spinner size="extra-tiny" />;
    case 'error':
      return <ErrorCircle24Regular style={{ fontSize: 16, color: tokens.colorStatusDangerForeground1 }} />;
    case 'pending':
    default:
      return <Clock24Regular style={{ fontSize: 16, color: tokens.colorNeutralForeground3 }} />;
  }
};

const DatasetTable: React.FC<{
  datasets: IngestionDatasetProgress[];
  labels: ReadonlyMap<string, string>;
  syncModes: ReadonlyMap<string, ConfiguredSyncMode>;
  isSyncing: boolean;
  isCancelled: boolean;
}> = ({ datasets, labels, syncModes, isSyncing, isCancelled }) => {
  if (datasets.length === 0) {
    return (
      <div className="pv-empty-datasets">
        <Caption1>No tables found.</Caption1>
      </div>
    );
  }

  return (
    <div className="pv-dataset-table">
      {/* Header */}
      <div className="pv-dataset-table-header">
        <div className="pv-dt-cell pv-dt-name">Table</div>
        <div className="pv-dt-cell pv-dt-mode">
          <Tooltip content="Configured sync mode, not the current run's execution mode. Incremental modes start with a full snapshot and may reload all rows after settings change or when incremental syncing is unavailable." relationship="description">
            <span tabIndex={0}>Sync mode</span>
          </Tooltip>
        </div>
        <div className="pv-dt-cell pv-dt-rows">{isSyncing ? 'Total Rows' : 'Rows'}</div>
        <div className="pv-dt-cell pv-dt-columns">Columns</div>
        {isSyncing && <div className="pv-dt-cell pv-dt-new-rows">New Rows</div>}
        <div className="pv-dt-cell pv-dt-status">Status</div>
        {isSyncing && <div className="pv-dt-cell pv-dt-progress">Progress</div>}
      </div>

      {/* Scrollable body */}
      <div className="pv-dataset-table-body">
        {datasets.map((ds) => {
          const display = ingestionDatasetDisplay(ds, isCancelled, labels);
          const syncMode = syncModes.get(ds.name);
          const dsStatus = display.status;
          const totalRows = ds.total_rows;
          const newRows = ds.row_count;

          return (
            <div key={ds.name} className="pv-dataset-table-row">
              <div className="pv-dt-cell pv-dt-name">
                <Text weight="semibold" size={200}>{labels.get(ds.name) || ds.name}</Text>
              </div>
              <div className="pv-dt-cell pv-dt-mode">
                <Text size={200}>{syncModeLabel(syncMode)}</Text>
              </div>
              <div className="pv-dt-cell pv-dt-rows">
                <Text size={200}>
                  {isSyncing
                    ? (totalRows != null ? totalRows.toLocaleString() : '—')
                    : (totalRows != null ? totalRows.toLocaleString() : (newRows != null ? newRows.toLocaleString() : '—'))}
                </Text>
              </div>
              <div className="pv-dt-cell pv-dt-columns">
                <Text size={200}>{display.columns}</Text>
              </div>
              {isSyncing && (
                <div className="pv-dt-cell pv-dt-new-rows">
                  <Text size={200} style={newRows && newRows > 0 ? { color: tokens.colorStatusSuccessForeground1 } : undefined}>
                    {newRows != null && newRows > 0 ? `+${newRows.toLocaleString()}` : '—'}
                  </Text>
                </div>
              )}
              <div className="pv-dt-cell pv-dt-status">
                <div className="pv-dt-status-content">
                  <DatasetStatusIcon status={dsStatus} />
                  {display.detail && !display.errorDetail ? (
                    <Tooltip content={display.detail} relationship="description">
                      <Text size={200} tabIndex={0}>{display.label}</Text>
                    </Tooltip>
                  ) : <Text size={200}>{display.label}</Text>}
                  {display.lastSync && (
                    <Caption1 className="pv-dt-last-sync" title={display.lastSync}>
                      · {display.lastSync}
                    </Caption1>
                  )}
                </div>
                {display.errorDetail && (
                  <Caption1 className="pv-dt-error">{display.errorDetail}</Caption1>
                )}
              </div>
              {isSyncing && (
                <div className="pv-dt-cell pv-dt-progress">
                  {dsStatus === 'syncing' || dsStatus === 'completed' ? (
                    <ProgressBar
                      value={dsStatus === 'completed' ? 1 : (ds.progress || 0)}
                      thickness="medium"
                      color={dsStatus === 'completed' ? 'success' : 'brand'}
                      style={{ minWidth: 80 }}
                    />
                  ) : dsStatus === 'error' ? (
                    <ProgressBar value={ds.progress || 0} thickness="medium" color="error" style={{ minWidth: 80 }} />
                  ) : (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProgressViewProps {
  connectionId: string;
  connectorId?: string;
  /** Called when user clicks "Edit Tables" */
  onEditTables?: () => void;
  /** Called when user clicks "Reconnect" after a re-auth error */
  onReconnect?: () => void;
  /** Page-level actions composed into the same header group as sync actions. */
  additionalTopBarActions?: readonly TopBarAction[];
}

// ---------------------------------------------------------------------------
// ProgressView (Main Component)
// ---------------------------------------------------------------------------

const CANCEL_TIMEOUT_MS = 10_000;

const ProgressView: React.FC<ProgressViewProps> = ({ connectionId, connectorId, onEditTables, onReconnect, additionalTopBarActions }) => {
  const navigate = useNavigate();
  const { isReadOnly, runMutation } = useWorkspaceAccess();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSyncStarting, setIsSyncStarting] = useState(false);
  const [isCreatingModel, setIsCreatingModel] = useState(false);
  const [labelState, setLabelState] = useState<{
    connectionId: string;
    labels: ReadonlyMap<string, string>;
    syncModes: ReadonlyMap<string, ConfiguredSyncMode>;
    error?: string;
  } | null>(null);
  const emptyLabels = useMemo(() => new Map<string, string>(), []);
  const emptyModes = useMemo(() => new Map<string, ConfiguredSyncMode>(), []);
  const currentLabels = labelState?.connectionId === connectionId
    ? labelState : null;

  useEffect(() => {
    let cancelled = false;
    connectionsService.getSelectedEntities(connectionId).then(({ entities }) => {
      if (!cancelled) setLabelState({ connectionId,
        labels: connectorId === 'postgresql' ? selectedEntityLabels(entities) : emptyLabels,
        syncModes: configuredSyncModes(entities) });
    }).catch(() => {
      if (!cancelled) setLabelState({
        connectionId, labels: emptyLabels, syncModes: emptyModes,
        error: 'Table settings could not be loaded. Sync modes are unavailable; table names use available metadata.',
      });
    });
    return () => { cancelled = true; };
  }, [connectionId, connectorId, emptyLabels, emptyModes]);

  // While isSyncStarting, polled data still shows the old run (status: 'ready'),
  // so the hook would use idleInterval (15s). Force activeInterval (2s) to detect
  // the new run quickly. Once isSyncStarting clears, normal interval logic resumes.
  const { status: s, error, refetch } = useIngestionProgress(connectionId, {
    idleInterval: isSyncStarting ? 2000 : undefined,
  });
  const datasetLabels = useMemo(
    () => ingestionDatasetLabels(s?.datasets ?? [], currentLabels?.labels ?? emptyLabels),
    [s?.datasets, currentLabels?.labels, emptyLabels],
  );
  const { connections, refreshConnections } = useConnections();
  const { createDataModel, setBuildJobId, updateDataModelStatusLocally } = useDataModels();
  const refreshedRef = useRef<{ initial: boolean; terminal: boolean }>({
    initial: false,
    terminal: false,
  });
  const [, setTick] = useState(0);
  const cancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRunIdRef = useRef<string | null>(null);

  const handleCancel = useCallback(() => {
    if (isCancelling) return;
    return runMutation(async () => {
      setIsCancelling(true);
      cancelTimerRef.current = setTimeout(() => {
        setIsCancelling(false);
        refetch();
      }, CANCEL_TIMEOUT_MS);
      try {
        await connectionsService.cancelIngestion(connectionId);
      } catch {
        if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
        setIsCancelling(false);
        refetch();
      }
    });
  }, [connectionId, isCancelling, refetch, runMutation]);

  // Clear cancel timeout when server confirms cancellation
  useEffect(() => {
    if (s?.stage === 'cancelled' && cancelTimerRef.current) {
      clearTimeout(cancelTimerRef.current);
      cancelTimerRef.current = null;
      setIsCancelling(false);
    }
    return () => {
      if (cancelTimerRef.current) clearTimeout(cancelTimerRef.current);
    };
  }, [s?.stage]);

  // Clear optimistic sync-starting flag once polling confirms the NEW run
  useEffect(() => {
    if (!isSyncStarting || !s?.run_id) return;
    // Still showing stale run data — keep optimistic "Starting sync…" state
    if (pendingRunIdRef.current && s.run_id !== pendingRunIdRef.current) return;
    // New run confirmed by polling
    pendingRunIdRef.current = null;
    setIsSyncStarting(false);
  }, [isSyncStarting, s?.run_id]);

  const handleTriggerSync = useCallback(() =>
    runMutation(async () => {
      // Set sentinel BEFORE the await so the useEffect guard works immediately
      pendingRunIdRef.current = '__pending__';
      setIsSyncStarting(true);
      refreshedRef.current.terminal = false;
      try {
        const result = await connectionsService.triggerSync(connectionId);
        pendingRunIdRef.current = result.runId;
      } catch {
        pendingRunIdRef.current = null;
        setIsSyncStarting(false);
        return;
      }
      refetch();
    }), [connectionId, refetch, runMutation]);

  // Refresh connections list on mount
  useEffect(() => {
    if (!refreshedRef.current.initial) {
      refreshedRef.current.initial = true;
      refreshConnections();
    }
  }, [refreshConnections]);

  // ─── Derived states ─────────────────────────────────────────────────
  const confirmedSyncing = s?.status === 'syncing';
  const isSyncing = confirmedSyncing || isSyncStarting;
  const isComplete = s?.status === 'ready';
  const isError = s?.status === 'error';
  const isReauthRequired = s?.status === 'reauth_required';
  const isCancelled = s?.stage === 'cancelled';
  const isTerminal = isComplete || isError || isCancelled || isReauthRequired;
  const datasets = ingestionProgressRows(s?.datasets || [], isSyncStarting);
  const completedCount = datasets.filter((d) => d.stage === 'done').length;
  const totalCount = datasets.length;

  // Track whether we observed a syncing→ready transition (first ingestion)
  const sawSyncingRef = useRef(false);
  if (isSyncing) {
    sawSyncingRef.current = true;
  }

  // ─── Auto-navigate: poll for data model after FIRST ingestion completes ───
  // Only runs when we witnessed syncing→ready (not on page reload of an idle connection).
  useEffect(() => {
    if (!isComplete || !sawSyncingRef.current) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30; // Poll for up to 60s (30 × 2s)

    const poll = async () => {
      if (cancelled || attempts >= maxAttempts) return;
      attempts++;
      try {
        const models = await dataModelsService.list();
        const match = models.find(m => m.connectionId === connectionId);
        if (match && !cancelled) {
          navigate(`/data-models/${match.id}`, { replace: true });
          return;
        }
      } catch {
        // ignore — keep polling
      }
      if (!cancelled) {
        setTimeout(poll, 2000);
      }
    };

    // Start polling after a short delay (give auto-pipeline time to fire)
    const timer = setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isComplete, connectionId, navigate]);

  // ─── Create Data Model directly (skip ConnectionPickerPanel) ────────
  const handleCreateDataModel = useCallback(() => {
    if (isCreatingModel) return;
    return runMutation(async () => {
      setIsCreatingModel(true);
      try {
        const conn = connections.find((c) => c.id === connectionId);
        const autoName = `${conn?.name ?? 'Data Source'} Model`;
        const model = await createDataModel(autoName, connectionId);
        navigate(`/data-models/${model.id}`);
        // Start build in background
        const { jobId } = await dataModelsService.build(model.id, { maxViews: 6 });
        setBuildJobId(model.id, jobId);
        updateDataModelStatusLocally(model.id, 'building');
      } catch {
        setIsCreatingModel(false);
      }
    });
  }, [isCreatingModel, connections, connectionId, createDataModel, navigate, runMutation, setBuildJobId, updateDataModelStatusLocally]);

  // Periodically re-render so "Last synced X ago" stays fresh
  useEffect(() => {
    if (!isSyncing) {
      const timer = setInterval(() => setTick((t) => t + 1), 60_000);
      return () => clearInterval(timer);
    }
  }, [isSyncing]);

  // ─── Top Bar Actions ────────────────────────────────────────────────
  const topBarActions = useMemo<readonly TopBarAction[]>(() => {
    if (isSyncing) {
      const actions: TopBarAction[] = [{
        id: 'create-data-model',
        label: isCreatingModel ? 'Creating…' : 'Create Data Model',
        appearance: 'primary',
        icon: <BrainCircuit24Regular />,
        disabled: isReadOnly || isCreatingModel,
        onClick: handleCreateDataModel,
      }];
      if (onEditTables) actions.push({
        id: 'edit-tables', label: 'Edit Tables', appearance: 'outline', icon: <TableEditRegular />,
        disabled: isReadOnly, onClick: () => { runMutation(onEditTables); },
      });
      actions.push(isSyncStarting && !confirmedSyncing ? {
        id: 'sync-status', label: 'Starting Sync…', appearance: 'secondary', icon: <Spinner size="tiny" />, disabled: true,
        onClick: () => undefined,
      } : {
        id: 'cancel-sync', label: isCancelling ? 'Cancelling…' : 'Cancel Sync', appearance: 'secondary', icon: <DismissCircle24Regular />,
        disabled: isReadOnly || isCancelling, onClick: handleCancel,
      });
      return [...actions, ...(additionalTopBarActions ?? [])];
    }
    if (isTerminal) {
      const actions: TopBarAction[] = [{
        id: 'create-data-model', label: isCreatingModel ? 'Creating…' : 'Create Data Model', appearance: 'primary',
        icon: <BrainCircuit24Regular />,
        disabled: isReadOnly || isCreatingModel || isReauthRequired, onClick: handleCreateDataModel,
      }];
      if (onEditTables) actions.push({
        id: 'edit-tables', label: 'Edit Tables', appearance: 'outline', icon: <TableEditRegular />,
        disabled: isReadOnly || isReauthRequired, onClick: () => { runMutation(onEditTables); },
      });
      actions.push({
        id: 'sync-now', label: 'Sync Now', appearance: 'outline', icon: <ArrowSync24Regular />,
        disabled: isReadOnly || isReauthRequired, onClick: handleTriggerSync,
      });
      return [...actions, ...(additionalTopBarActions ?? [])];
    }
    return additionalTopBarActions ?? [];
  }, [isSyncing, isSyncStarting, confirmedSyncing, isTerminal, isReauthRequired, isCancelling, isCreatingModel, isReadOnly, handleCancel, handleCreateDataModel, handleTriggerSync, onEditTables, runMutation, additionalTopBarActions]);

  useTopBarActionItems(topBarActions);

  // Refresh connections when terminal
  useEffect(() => {
    if ((isComplete || isCancelled) && !refreshedRef.current.terminal) {
      refreshedRef.current.terminal = true;
      refreshConnections();
    }
  }, [isComplete, isCancelled, refreshConnections]);

  // ─── Loading / Error ────────────────────────────────────────────────
  if (!s && !error) {
    return (
      <div className="progress-loading">
        <Spinner size="large" label="Loading status…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-error">
        <ErrorCircle24Regular style={{ fontSize: 48, color: tokens.colorStatusDangerForeground1 }} />
        <Body1>Failed to load status</Body1>
        <Caption1>{error}</Caption1>
      </div>
    );
  }

  const status = s!;

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="pv-root">
      {/* Inline status row */}
      <div className="pv-status-row">
        <StatusIndicator
          isSyncing={!!isSyncing}
          isSyncStarting={isSyncStarting && !confirmedSyncing}
          isError={!!isError}
          isReauthRequired={!!isReauthRequired}
          wasCancelled={s?.stage === 'cancelled'}
          completedCount={completedCount}
          totalCount={totalCount}
          lastSyncedAt={status.updated_at}
        />
      </div>

      {/* Re-auth required banner */}
      {isReauthRequired && (
        <div className="pv-reauth-banner">
          <div className="pv-reauth-banner-content">
            <LockClosed24Regular style={{ fontSize: 20, color: tokens.colorStatusWarningForeground1 }} />
            <div className="pv-reauth-banner-text">
              <Text weight="semibold" size={300}>Authentication expired</Text>
              <Caption1>Your connection credentials have expired. Please reconnect to resume syncing.</Caption1>
            </div>
          </div>
          {onReconnect && (
            <MutationButton appearance="primary" onClick={() => runMutation(onReconnect)}>
              Reconnect
            </MutationButton>
          )}
        </div>
      )}

      {/* Sync info panel (idle only, hidden on reauth) */}
      {!isSyncing && !isReauthRequired && (
        <SyncInfoPanel
          startedAt={status.started_at}
          updatedAt={status.updated_at}
          datasets={datasets}
          totalRowCount={status.total_row_count ?? undefined}
          error={datasetMessageDisplayName(status.error, datasetLabels)}
        />
      )}

      {/* Overall progress bar (syncing only, after confirmed) */}
      {isSyncing && confirmedSyncing && (
        <SyncProgressBar
          progress={status.progress || 0}
          completedCount={completedCount}
          totalCount={totalCount}
        />
      )}

      {/* Starting sync placeholder */}
      {isSyncStarting && !confirmedSyncing && (
        <div className="pv-sync-info" style={{ justifyContent: 'center' }}>
          <Spinner size="small" label="Connecting to data source…" />
        </div>
      )}

      {/* Keep all rows visible while a new run starts; only reauth hides the table. */}
      {currentLabels?.error && <Caption1 role="status">{currentLabels.error}</Caption1>}
      {!isReauthRequired && (
        <DatasetTable
          datasets={datasets}
          labels={datasetLabels}
          syncModes={currentLabels?.syncModes ?? emptyModes}
          isSyncing={!!isSyncing}
          isCancelled={!!isCancelled}
        />
      )}

    </div>
  );
};

export default ProgressView;